"use client";

import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { ShieldCheck, Star, Globe, Award, Briefcase, MapPin, DollarSign, X } from "lucide-react";
import type { Contractor } from "@/lib/types";
import { api } from "@/lib/api";

interface ContractorReview {
  id: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  customerName: string;
}

export function ContractorProfileModal({ contractor, onClose }: { contractor: Contractor; onClose: () => void }) {
  const [mounted, setMounted] = useState(false);
  const [reviews, setReviews] = useState<ContractorReview[]>([]);

  useEffect(() => {
    setMounted(true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    api.getContractorReviews(contractor.id)
      .then((result) => setReviews(result as ContractorReview[]))
      .catch(() => setReviews([]));
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [contractor.id]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-7 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button onClick={onClose} className="absolute right-5 top-5 rounded-xl bg-slate-100 p-2.5 text-slate-500 hover:bg-slate-200">
          <X size={16} />
        </button>

        <div className="flex items-start gap-4 pr-10">
          {contractor.logo_url ? (
            <img src={contractor.logo_url} alt={contractor.name} className="h-16 w-16 shrink-0 rounded-2xl border border-slate-200 object-cover" />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-slate-200 text-xl font-black text-slate-500">
              {contractor.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-lg font-black text-slate-950">{contractor.name}</h3>
              {contractor.isVerified && <ShieldCheck size={16} className="shrink-0 text-emerald-500" />}
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
              {contractor.trade} &bull; {contractor.businessType}
            </p>
            {contractor.headline && <p className="mt-1 text-sm font-semibold text-slate-600">{contractor.headline}</p>}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {(contractor.hourlyRateMin != null || contractor.hourlyRateMax != null) && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
              <DollarSign size={12} />
              {contractor.hourlyRateMin != null && contractor.hourlyRateMax != null
                ? `A$${contractor.hourlyRateMin}–A$${contractor.hourlyRateMax}/hr`
                : `A$${contractor.hourlyRateMin ?? contractor.hourlyRateMax}/hr`}
            </span>
          )}
          {typeof contractor.rating === "number" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700">
              <Star size={12} className="fill-amber-400 text-amber-400" />
              {contractor.rating.toFixed(1)}{contractor.reviewCount ? ` (${contractor.reviewCount} reviews)` : ""}
            </span>
          )}
          {!!contractor.hiredCount && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">
              <Briefcase size={12} /> {contractor.hiredCount} jobs completed
            </span>
          )}
          {contractor.isLicensed && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
              <Award size={12} /> Licensed
            </span>
          )}
          {contractor.location && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">
              <MapPin size={12} /> {contractor.location}
            </span>
          )}
        </div>

        {contractor.about && (
          <div className="mt-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">About</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{contractor.about}</p>
          </div>
        )}

        {!!contractor.fixedPricing?.length && (
          <div className="mt-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Fixed Price List</p>
            <div className="mt-1.5 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-100">
              {contractor.fixedPricing.map((entry, index) => (
                <div key={index} className="flex items-center justify-between bg-slate-50/50 px-4 py-2.5">
                  <span className="text-sm font-semibold text-slate-700">{entry.label}</span>
                  <span className="text-sm font-black text-emerald-700">A${entry.price}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!!contractor.licenses?.length && (
          <div className="mt-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Licenses &amp; Certifications</p>
            <ul className="mt-1.5 space-y-1">
              {contractor.licenses.map((license, index) => (
                <li key={index} className="text-sm font-semibold text-slate-600">&bull; {license}</li>
              ))}
            </ul>
          </div>
        )}

        {!!contractor.companyImages?.length && (
          <div className="mt-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Portfolio</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {contractor.companyImages.slice(0, 9).map((url, index) => (
                <img key={index} src={url} alt="Portfolio work" className="h-20 w-full rounded-xl border border-slate-200 object-cover" />
              ))}
            </div>
          </div>
        )}

        {!!reviews.length && (
          <div className="mt-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Customer Reviews</p>
            <div className="mt-1.5 space-y-3">
              {reviews.map((review) => (
                <div key={review.id} className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-black text-slate-800">{review.customerName}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700">
                      <Star size={12} className="fill-amber-400 text-amber-400" /> {review.rating}/5
                    </span>
                  </div>
                  {review.comment && <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{review.comment}</p>}
                  <p className="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {contractor.website && (
          <a
            href={contractor.website.startsWith("http") ? contractor.website : `https://${contractor.website}`}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-xs font-bold text-emerald-700 hover:underline"
          >
            <Globe size={14} /> Visit website
          </a>
        )}
      </div>
    </div>,
    document.body,
  );
}
